import gql from 'graphql-tag';

export const APPLIED_PLANS_PARTS = gql`
  fragment AppliedPlansParts on Plan {
    id
    name
  }
`;

export const COUPON_FRAGMENT = gql`
  fragment CouponParts on PlanCoupon {
    id
    name
    amountOff
    appliesToPlans {
      ...AppliedPlansParts
    }
    setupFeeOff
  }
  ${APPLIED_PLANS_PARTS}
`;

export const SUBSCRIPTION_FRAGMENT = gql`
  fragment SubscriptionParts on Subscription {
    name
    productCode
    amount
    status
    coupon {
      ...CouponParts
    }
    startDate
    endDate
    stripeId
    stripePriceId
    createdAt
    isDrp
  }

  ${COUPON_FRAGMENT}
`;

export const PLAN_LOCK_FRAGMENT = gql`
  fragment BillingPlanParts on PlanLock {
    active
    couponId
    plan
    setupFee
    trialDuration
  }
`;

export const COMPANY_DELINQUENCY_FRAGMENT = gql`
  fragment CompanyDelinquencyParts on CompanyDelinquency {
    delinquent
    delinquentSince
    lockout
    defaultCardVerificationStatus
  }
`;

export const PAYMENT_SOURCE_FRAGMENT = gql`
  fragment PaymentSourceParts on PaymentSource {
    addressCity
    addressCountry
    addressLine1
    addressLine2
    addressState
    addressZip
    brand
    country
    cvcCheck
    default
    dynamicLast4
    expMonth
    expYear
    funding
    id
    last4
    name
  }
`;

export const PRODUCT_FRAGMENT = gql`
  fragment BillingProductParts on Product {
    id
    name
    key
    creditBalance
    price
    subscriptionPrice
    activeSubscription
  }
`;

export const PRODUCT_RESULT_FRAGMENT = gql`
  fragment ProductResultParts on PurchaseResult {
    createdAt
    creditsPurchased
    productKey
    id
    status
  }
`;

export const PAYMENT_SOURCES_FRAGMENT = gql`
  fragment PaymentSourcesParts on PaymentSources {
    companyId
    sources {
      ...PaymentSourceParts
    }
  }

  ${PAYMENT_SOURCE_FRAGMENT}
`;

export const GET_COUPON_QUERY = gql`
  query GetCouponById($data: GetCouponInput!) {
    coupon(data: $data) {
      ...CouponParts
    }
  }
  ${COUPON_FRAGMENT}
`;

export const GET_LOCKED_PLAN_QUERY = gql`
  query getLockedPlan($companyId: String!) {
    getPlanLock(companyId: $companyId) {
      ...BillingPlanParts
    }
  }

  ${PLAN_LOCK_FRAGMENT}
`;

// TODO - replace name in consuming context, updated to include Billing
export const GET_COMPANY_SUBSCRIPTIONS_QUERY = gql`
  query GetBillingCompanySubscriptions($data: GetCompanySubscriptionsInput!) {
    companySubscriptions(data: $data) {
      ...SubscriptionParts
    }
  }

  ${SUBSCRIPTION_FRAGMENT}
`;

export const GET_MY_PRODUCTS = gql`
  query myProducts {
    myProducts {
      ...BillingProductParts
    }
  }

  ${PRODUCT_FRAGMENT}
`;

export const PURCHASE_CREDITS = gql`
  mutation purchaseCredits($data: PurchaseInput!) {
    purchaseCredits(data: $data) {
      ...ProductResultParts
    }
  }

  ${PRODUCT_RESULT_FRAGMENT}
`;

export const PURCHASE_SUBSCRIPTION = gql`
  mutation purchaseSubscription($data: PurchaseSubscriptionInput!) {
    purchaseSubscription(data: $data) {
      ...ProductResultParts
    }
  }

  ${PRODUCT_RESULT_FRAGMENT}
`;

export const GET_COMPANY_DELINQUENCY = gql`
  query GetCompanyDelinquency($data: GetCompanyDelinquency!) {
    getCompanyDelinquency(data: $data) {
      ...CompanyDelinquencyParts
    }
  }

  ${COMPANY_DELINQUENCY_FRAGMENT}
`;

export const GET_COMPANY_SOURCES = gql`
  query GetCompanySources($data: GetCompanyPaymentSources!) {
    getCompanySources(data: $data) {
      ...PaymentSourcesParts
    }
  }

  ${PAYMENT_SOURCE_FRAGMENT}
`;

export const UPDATE_PAYMENT_SOURCE_BILLING_ADDRESS = gql`
  mutation UpdatePaymentSourceBillingAddress(
    $input: UpdatePaymentSourceBillingAddress!
  ) {
    updatePaymentSourceBillingAddress(input: $input) {
      ...PaymentSourceParts
    }
  }

  ${PAYMENT_SOURCE_FRAGMENT}
`;

export const SCRATCH_LIST_SERVICE_ADDONS = gql`
  query ScratchListServiceAddons($input: ScratchServiceAddonListInput!) {
    scratchListServiceAddons(input: $input) {
      id
      label
      priceLabel
      enabled
    }
  }
`;

export const SCRATCH_TOGGLE_SERVICE_ADDON = gql`
  mutation ScratchToggleServiceAddon($input: ScratchToggleServiceAddonInput!) {
    scratchToggleServiceAddon(input: $input) {
      id
      label
      priceLabel
      enabled
    }
  }
`;

// Billing history for a company
export const GET_BILLING_HISTORY = gql`
  query GetBillingHistory($companyId: String!) {
    getBillingHistory(companyId: $companyId) {
      id
      createdAt
      modifiedAt
      amount
      productKey
      statementType
      status
      hostedUrl
      pdfUrl
      stripeSubscriptionId
    }
  }
`;

// Payment methods for a company (used by ProductSelector + Settings)
export const GET_BILLING_INFO_PAYMENT_METHODS = gql`
  query GetBillingInfoPaymentMethods($input: GetBillingInfoInput!) {
    getBillingInfo(input: $input) {
      paymentMethods {
        id
        default
        status
        type
        name
        issueReason
        nextAction
        expirationDate
      }
    }
  }
`;

// Only what plan access needs to resolve a company's core plan (BIO-2085):
// the Recurly-only path and the migrated-company fallback in usePlanAccess and
// getPlanPackage. Kept separate from GET_BILLING_INFO so the provider and state
// fields, which need billing-service BIO-2074 (#1500) in the gateway, cannot
// fail Plan Info, the paused banner or cancellation status.
export const GET_CORE_PLAN_ACCESS = gql`
  query GetCorePlanAccess($input: GetBillingInfoInput!) {
    getBillingInfo(input: $input) {
      id
      coreSubscription {
        provider
        state
        active
        plan {
          productKey
          legacyProductKey
        }
      }
    }
  }
`;

// Full billing info including pause status
export const GET_BILLING_INFO = gql`
  query GetBillingInfo($input: GetBillingInfoInput!) {
    getBillingInfo(input: $input) {
      id
      pause {
        id
        isPaused
        reactivatesOn
      }
      coreSubscription {
        active
        cancellationRequested
        cancelsAt
        periodEndsAt
        plan {
          name
          productKey
          legacyProductKey
          billingPeriod
          price
          employeeSizeMapping
          value
        }
        scheduledPlanChange {
          effectiveOn
          newPlan {
            name
          }
        }
      }
      addonSubscriptions {
        active
        cancelsAt
        quantity
        plan {
          name
          billingPeriod
          price
          serviceKey
        }
        periodEndsAt
      }
    }
  }
`;

// When the current term ends, for the confirm cancellation modal. getBillingInfo
// maps the core subscription's bill-cycle end, which for a Recurly annual term
// billed monthly is the end of the current month, months before the term the
// cancellation actually waits for. The v2 read exposes the term end and returns
// null for Stripe-billed companies, so the modal falls back to the cycle end
// there. no-cache: the term end moves after each renewal.
export const GET_BILLING_SUBSCRIPTION_TERM_END = gql`
  query GetBillingSubscriptionTermEnd($companyId: ID!) {
    billingSubscription(companyId: $companyId) {
      currentTermEndsAt
    }
  }
`;

export const CONFIRM_MICRO_DEPOSIT = gql`
  mutation ConfirmMicroDeposit($input: ConfirmMicroDepositInput!) {
    confirmMicroDeposit(input: $input) {
      id
      default
      status
      type
      name
      expirationDate
    }
  }
`;

export const GET_COMPANY_SUBSCRIPTIONS = gql`
  query GetCompanySubscriptions($data: GetCompanySubscriptionsInput!) {
    companySubscriptions(data: $data) {
      name
      productCode
      amount
      status
      startDate
      endDate
      stripeId
      stripePriceId
      createdAt
      isDrp
    }
  }
`;

// Remove a payment method
export const REMOVE_PAYMENT_METHOD = gql`
  mutation RemovePaymentMethod($input: RemovePaymentMethodInput!) {
    removePaymentMethod(input: $input)
  }
`;

// ---- Recurly (v2) surface. BIO-1920: app migrates off the retiring v1
// operations one screen at a time; a Stripe-billed company keeps the v1
// operations above, a Recurly-billed company reads and writes through these.
// The v2 mutations are contract-first (BIO-1914): billing-service ships the
// schema before the resolvers, so a write can answer NotImplemented until the
// resolver lands. Nothing here deletes or alters the v1 operations.

// Payment methods for a company, Recurly wallet (BIO-2095). A Stripe-billed
// company (no RECURLY billing_account) gets an empty list from billing-service.
export const GET_RECURLY_PAYMENT_METHODS = gql`
  query RecurlyPaymentMethods($companyId: ID!) {
    paymentMethods(companyId: $companyId) {
      id
      kind
      brand
      last4
      expMonth
      expYear
      primary
      backup
      bankName
      accountType
      verificationStatus
    }
  }
`;

// Add a payment method from a Recurly token (Recurly.js tokenized the card or
// bank account client-side; the raw number never reaches us). When the issuer
// wants a 3-D Secure challenge the mutation comes back requiresAction with an
// actionTokenId; the client runs Recurly.js Risk().ThreeDSecure and retries
// with threeDSecureActionResultTokenId (BIO-2252).
export const ADD_RECURLY_PAYMENT_METHOD = gql`
  mutation AddRecurlyPaymentMethod($input: AddPaymentMethodInput!) {
    addPaymentMethod(input: $input) {
      requiresAction
      actionTokenId
      paymentMethod {
        id
        primary
      }
    }
  }
`;

// Set the payment method Recurly charges first.
export const SET_PRIMARY_RECURLY_PAYMENT_METHOD = gql`
  mutation SetPrimaryRecurlyPaymentMethod($input: PaymentMethodRef!) {
    setPrimaryPaymentMethod(input: $input) {
      id
      primary
    }
  }
`;

// Remove a payment method (v1 and v2 collide on the name, so the v2 operation
// carries the V2 suffix; BIO-1914).
export const REMOVE_RECURLY_PAYMENT_METHOD = gql`
  mutation RemoveRecurlyPaymentMethod($input: PaymentMethodRef!) {
    removePaymentMethodV2(input: $input)
  }
`;

// Billing history for a company, Recurly invoices newest first (BIO-2096).
// `first` is one Recurly page (clamped 1-200 server-side); the billing-history
// screen was unpaginated under v1, so it reads one large page for parity.
export const GET_RECURLY_INVOICES = gql`
  query RecurlyInvoices($companyId: ID!, $first: Int!) {
    invoices(companyId: $companyId, first: $first) {
      hasNextPage
      endCursor
      nodes {
        id
        number
        state
        type
        createdAt
        paidAt
        total {
          amount
          currency
        }
        pdfUrl
        lines {
          description
          planCode
          addOnCode
          type
        }
      }
    }
  }
`;

// Set default billing payment method
export const SET_DEFAULT_BILLING_PAYMENT_METHOD = gql`
  mutation SetDefaultBillingPaymentMethod(
    $input: SetDefaultPaymentMethodInput!
  ) {
    setDefaultPaymentMethod(input: $input) {
      id
      default
      status
      type
      name
      expirationDate
    }
  }
`;

// Cancel Bambee Plus subscription
// Removed Bambee Plus cancellation (entity removed)

export const GET_AVAILABLE_PRODUCTS = gql`
  query GetAvailableProducts($data: GetAvailableProductsInput!) {
    getAvailableProducts(data: $data) {
      id
      key
      name
      tier
      billingPeriod
      monthlyAllowance
      subscriptionPrice
      price
      allowance
      discountedPrice
    }
  }
`;

export const GET_COMPANY_CREDITS = gql`
  query GetCompanyCredits($data: GetCompanyCreditsInputV2!) {
    getCompanyCreditsV2(data: $data) {
      id
      createdAt
      productKey
      creditType
    }
  }
`;

export const REACTIVATE_PAUSED_COMPANY = gql`
  mutation ReactivatePausedCompany($input: ReactivatePausedCompanyInput!) {
    reactivatePausedCompany(input: $input)
  }
`;

// Premium People add-on plan for the authed user's company (drives the price on
// the upgrade surface). companyId is optional; the billing service falls back
// to the auth user's company when it's omitted.
export const PREMIUM_PEOPLE_ADDON_PLAN_FOR_COMPANY = gql`
  query PremiumPeopleAddonPlanForCompany($companyId: String) {
    premiumPeopleAddonPlanForCompany(companyId: $companyId) {
      serviceKey
      productKey
      name
      value
      priceId
      price
      billingPeriod
      status
    }
  }
`;

// Premium People Core plan catalog (BIO-1314, billing PR #1453). Unlike getPricingPackages this is
// independent of the company's current subscription, so it works for a brand-new lead with no plan
// and no known headcount — which is exactly the /trial/premium-people case. Omit companyId (pass
// null) to get every tier and billing period; pass a companyId to narrow to the tier covering that
// company's W2 employee count.
export const PREMIUM_PEOPLE_CORE_PLANS = gql`
  query PremiumPeopleCorePlans($companyId: ID) {
    premiumPeopleCorePlans(companyId: $companyId) {
      priceId
      productKey
      name
      value
      billingPeriod
      tierNumber
      minSize
      maxSize
      price
      monthlyPrice
      status
    }
  }
`;

// Charges the card on file and creates the Premium People add-on subscription.
// Called from the "Confirm & Activate" CTA.
export const CREATE_PREMIUM_PEOPLE_ADDON_SUBSCRIPTION = gql`
  mutation CreatePremiumPeopleAddonSubscription(
    $data: CreatePremiumPeopleAddonInput!
  ) {
    createPremiumPeopleAddonSubscription(data: $data) {
      name
      status
      stripeId
      stripePriceId
      productCode
      planValue
      amount
      startDate
      endDate
      isDrp
    }
  }
`;
